import { CustomIcon } from '@/components/CustomIcon';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar';
import { useRootSelector } from '@/hooks/selector.hook';
import { Link, useNavigate } from 'react-router-dom';

type MainSideBarProps = {
  isCollapsed: boolean;
  menuItems: MenuItem[];
  setHoveredItem: (label: string | null) => void;
  isActive: (pageTitle: string | null | undefined) => boolean;
  setCurrentView: (view: 'MAIN' | 'SETTINGS') => void;
  isDark: boolean;
  hoveredItem: string | null;
  isMobile: boolean;
  setOpenMobile: (open: boolean) => void;
};

const MainSideBar = ({
  isCollapsed,
  menuItems,
  setHoveredItem,
  isActive,
  setCurrentView,
  isDark,
  hoveredItem,
  isMobile,
  setOpenMobile,
}: MainSideBarProps) => {
  const sections = useRootSelector((state) => state.permission.sections);
  const navigate = useNavigate();

  const getIconFilterStyle = (active: boolean, isDark: boolean, isHovered: boolean = false) => {
    if (active || isHovered) {
      return {
        filter:
          'invert(96%) sepia(63%) saturate(876%) hue-rotate(80deg) brightness(96%) contrast(90%)',
      };
    }
    if (isDark) {
      return {
        filter:
          'invert(96%) sepia(4%) saturate(15%) hue-rotate(156deg) brightness(107%) contrast(100%)',
      };
    }
    return {};
  };
  return (
    <>
      <SidebarMenu className="flex flex-col gap-[1.2rem] mt-[3rem]">
        {menuItems.map((menu, index) => (
          <div key={index}>
            <p
              className={`font-montserrat ${isCollapsed ? 'text-[1.5rem] text-center' : 'text-[1.8rem]'
                } font-medium leading-[100%] tracking-[0%]`}
            >
              {isCollapsed ? menu.label.split(' ')[0] : menu.label}
            </p>
            <div
              className={`${isCollapsed ? 'pl-[0] items-center' : 'lg:pl-[1rem] pl-[0]'
                } mt-[2rem] flex flex-col gap-[1.2rem]`}
            >
              {menu.type === 'Section' && (
                <>
                  {menu.children?.map((item, index) => {
                    const isDisabled = item.label === 'Profile';

                    return (
                      <SidebarMenuItem key={index}>
                        <SidebarMenuButton
                          asChild
                          aria-disabled={isDisabled}
                          disabled={isDisabled}
                          tooltip={isCollapsed && index > 0 ? item.label : undefined}
                          className={`text-[1.5rem] family-[montserrat] md:px-[1.2rem] ${!isCollapsed && 'rounded-[1.2rem]'
                            } ${isDisabled ? 'cursor-not-allowed' : ''}`}
                        >
                          {isDisabled ? (
                            <span
                              className={`group/item flex h-[5rem] ${isActive(item.label)
                                ? 'dark:!bg-[#57E8BA14] !bg-[#FFFFFF]'
                                : 'dark:bg-transparent'
                                } ${isCollapsed ? 'justify-center items-center' : 'gap-[1rem]'}`}
                              onMouseEnter={() => setHoveredItem(item.label)}
                              onMouseLeave={() => setHoveredItem(null)}
                            >
                              {/* ICON */}
                              <div
                                className={`icon border py-[0.8rem] min-w-[3.5rem] min-h-[3.5rem] px-[0.6rem] rounded-[0.8rem] flex items-center justify-center flex-shrink-0 ${isActive(item.label)
                                  ? 'dark:border-[#57E8BA] border-[#000000]'
                                  : 'border-transparent'
                                  }`}
                              >
                                {item.icon && (
                                  <div
                                    className="transition-all ease duration-300"
                                    style={getIconFilterStyle(
                                      isActive(item.label),
                                      isDark,
                                      hoveredItem === item.label,
                                    )}
                                  >
                                    <CustomIcon
                                      type={item.icon}
                                      className={`transition-all w-[2rem] ease duration-300 ${isActive(item.label)
                                        ? 'dark:text-[#57E8BA] text-black'
                                        : 'dark:text-[#FFFFFF] text-black'
                                        }`}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* TITLE */}
                              <span
                                className={`${isCollapsed && index > 0 ? 'hidden' : ''} ${isCollapsed ? 'hidden' : 'flex items-center'
                                  } transition-all ease duration-300 ${isActive(item.label)
                                    ? 'dark:text-[#57E8BA] text-black'
                                    : 'dark:text-[#FFFFFF] text-black'
                                  }`}
                              >
                                {item.label}
                              </span>
                            </span>
                          ) : (
                            <Link
                              to={item.route || '#'}
                              className={`group/item flex h-[5rem] ${isActive(item.label)
                                ? 'dark:!bg-[#57E8BA14] !bg-[#FFFFFF] shadow-[0px_0px_15px_-10px_rgba(0,0,0,1)]'
                                : 'dark:bg-transparent'
                                } ${isCollapsed ? 'justify-center items-center' : 'gap-[1rem]'}`}
                              onMouseEnter={() => setHoveredItem(item.label)}
                              onMouseLeave={() => setHoveredItem(null)}
                              onClick={(e) => {
                                if (item.label === 'Settings') {
                                  e.preventDefault();
                                  setCurrentView('SETTINGS');
                                  const firstRoute = sections?.[0]?.items?.[0]?.route;
                                  if (firstRoute) navigate(firstRoute);
                                  return;
                                }

                                if (isMobile) {
                                  setOpenMobile(false);
                                }
                              }}
                            >
                              {/* ICON */}
                              <div
                                className={`icon border py-[0.8rem] min-w-[3.5rem] min-h-[3.5rem] px-[0.6rem] rounded-[0.8rem] flex items-center justify-center flex-shrink-0 ${isActive(item.label)
                                  ? 'dark:border-[#57E8BA] border-[#000000]'
                                  : 'border-transparent'
                                  }`}
                              >
                                {item.icon && (
                                  <div
                                    className="transition-all ease duration-300"
                                    style={getIconFilterStyle(
                                      isActive(item.label),
                                      isDark,
                                      hoveredItem === item.label,
                                    )}
                                  >
                                    <CustomIcon
                                      type={item.icon}
                                      className={`group-hover/item:text-[#57E8BA] w-[2rem] transition-all ease duration-300 ${isActive(item.label)
                                        ? 'dark:text-[#57E8BA] text-black'
                                        : 'dark:text-[#FFFFFF] text-black'
                                        }`}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* TITLE */}
                              <span
                                className={`${isCollapsed && index > 0 ? 'hidden' : ''} ${isCollapsed ? 'hidden' : 'flex items-center'
                                  } group-hover/item:text-[#57E8BA] transition-all ease duration-300 ${isActive(item.label)
                                    ? 'dark:text-[#57E8BA] text-black'
                                    : 'dark:text-[#FFFFFF] text-black'
                                  }`}
                              >
                                {item.label}
                              </span>
                            </Link>
                          )}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        ))}
      </SidebarMenu>
    </>
  );
};

export default MainSideBar;
